import { AlertTriangle, Ban, CheckCircle2, FileQuestion, Flag, Image as ImageIcon, MessageSquare, User } from 'lucide-react'

import { AgeIndicator as SharedAgeIndicator, badgeBase, formatAge, formatDateTime, hoursSince, InitialsAvatar } from '../ui/queueUi'
import type {
  ModerationCaseStatus,
  ModerationDecision,
  ModerationInterimAction,
  ModerationQueueType,
  ModerationReportReason,
  ModerationTargetType,
  ReporterTrustTier
} from '../../lib/api/moderation'

export const REASON_LABELS: Record<ModerationReportReason, string> = {
  HARASS: 'Harassment / bullying',
  HATE: 'Hate speech',
  SEX: 'Sexual content',
  VIOL: 'Violence / threats',
  DOXX: 'Private info (doxxing)',
  SHARM: 'Self-harm risk',
  DANG: 'Dangerous activity',
  SPAM: 'Spam',
  OTHER: 'Other'
}

export const TARGET_LABELS: Record<ModerationTargetType, string> = {
  Post: 'Post',
  Comment: 'Comment',
  ImageAsset: 'Photo',
  User: 'User profile'
}

/** Reasons where the backend rejects a removal without a strike. */
export const STRIKE_REQUIRED_REASONS: ModerationReportReason[] = ['DOXX', 'HATE', 'SHARM']

export const DECISION_OPTIONS: Array<{
  value: ModerationDecision
  label: string
  description: string
  tone: 'neutral' | 'warning' | 'danger'
  appliesStrike: boolean
}> = [
  {
    value: 'DISMISS',
    label: 'Dismiss',
    description: 'No violation. Restore the content and count the reports as overturned.',
    tone: 'neutral',
    appliesStrike: false
  },
  {
    value: 'REMOVE',
    label: 'Remove',
    description: 'Keep the content hidden. No strike on the author.',
    tone: 'warning',
    appliesStrike: false
  },
  {
    value: 'REMOVE_STRIKE',
    label: 'Remove + strike',
    description: 'Keep the content hidden and add a strike to the author.',
    tone: 'warning',
    appliesStrike: true
  },
  {
    value: 'REMOVE_LOCK',
    label: 'Remove + lock thread',
    description: 'Keep the content hidden and turn off comments on the post it happened in. No strike.',
    tone: 'warning',
    appliesStrike: false
  },
  {
    value: 'SUSPEND_24H',
    label: 'Suspend 24h',
    description: 'Remove, strike, and suspend posting for 24 hours.',
    tone: 'danger',
    appliesStrike: true
  },
  {
    value: 'SUSPEND_7D',
    label: 'Suspend 7d',
    description: 'Remove, strike, and suspend posting for a week.',
    tone: 'danger',
    appliesStrike: true
  },
  {
    value: 'BAN',
    label: 'Ban',
    description: 'Remove the content and ban the account from posting.',
    tone: 'danger',
    appliesStrike: true
  }
]

export const decisionLabel = (decision?: ModerationDecision | null) =>
  DECISION_OPTIONS.find((option) => option.value === decision)?.label ?? decision ?? 'Unknown'

export { formatAge, formatDateTime, hoursSince, InitialsAvatar }

export const caseRef = (id: string) => `#${id.slice(-6).toUpperCase()}`

export const queueRailClass = (queue: ModerationQueueType) => {
  switch (queue) {
    case 'PRIORITY':
      return 'border-l-red-500'
    case 'APPEALS':
      return 'border-l-indigo-500'
    default:
      return 'border-l-stone-300'
  }
}

export function QueueBadge({ queue }: { queue: ModerationQueueType }) {
  const classes =
    queue === 'PRIORITY'
      ? 'bg-red-100 text-red-800'
      : queue === 'APPEALS'
        ? 'bg-indigo-100 text-indigo-800'
        : 'bg-stone-100 text-stone-600'
  return <span className={`${badgeBase} ${classes}`}>{queue === 'STANDARD' ? 'Standard' : queue === 'PRIORITY' ? 'Priority' : 'Appeal'}</span>
}

export function StatusBadge({ status }: { status: ModerationCaseStatus }) {
  return (
    <span className={`${badgeBase} ${status === 'Open' ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-600'}`}>
      {status}
    </span>
  )
}

export function ReasonBadge({ reason }: { reason: ModerationReportReason }) {
  const classes = ['HATE', 'SHARM', 'DOXX', 'SEX', 'VIOL'].includes(reason)
    ? 'border-red-200 bg-red-50 text-red-700'
    : ['HARASS', 'DANG'].includes(reason)
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-stone-200 bg-stone-50 text-stone-600'
  return <span className={`${badgeBase} border ${classes}`}>{REASON_LABELS[reason] ?? reason}</span>
}

export function TrustTierBadge({ tier }: { tier: ReporterTrustTier }) {
  const meta =
    tier === 'TRUSTED'
      ? { label: 'Trusted reporter', classes: 'border-emerald-200 bg-emerald-50 text-emerald-700' }
      : tier === 'NEW'
        ? { label: 'New reporter', classes: 'border-amber-200 bg-amber-50 text-amber-700' }
        : { label: 'Standard reporter', classes: 'border-stone-200 bg-stone-50 text-stone-600' }
  return <span className={`${badgeBase} border ${meta.classes}`}>{meta.label}</span>
}

export function InterimActionBadge({ action }: { action: ModerationInterimAction }) {
  if (action === 'None') {
    return <span className={`${badgeBase} bg-emerald-50 text-emerald-700`}>Visible in feeds</span>
  }
  return (
    <span className={`${badgeBase} ${action === 'HardHide' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
      {action === 'HardHide' ? 'Hidden everywhere' : 'Hidden from feeds'}
    </span>
  )
}

/** Age chip whose color escalates as an open case sits unhandled. */
export function AgeIndicator({ createdAt, status }: { createdAt: string; status: ModerationCaseStatus }) {
  return <SharedAgeIndicator since={createdAt} muted={status === 'Closed'} />
}

export function TargetTypeIcon({ targetType, className = 'h-4 w-4' }: { targetType: ModerationTargetType; className?: string }) {
  switch (targetType) {
    case 'Post':
      return <Flag className={className} />
    case 'Comment':
      return <MessageSquare className={className} />
    case 'ImageAsset':
      return <ImageIcon className={className} />
    case 'User':
      return <User className={className} />
    default:
      return <FileQuestion className={className} />
  }
}

export function DecisionToneIcon({ tone, className = 'h-4 w-4' }: { tone: 'neutral' | 'warning' | 'danger'; className?: string }) {
  if (tone === 'danger') return <Ban className={`${className} text-red-600`} />
  if (tone === 'warning') return <AlertTriangle className={`${className} text-amber-600`} />
  return <CheckCircle2 className={`${className} text-emerald-600`} />
}
